import React, {
    createContext, useCallback, useContext, useMemo, useState,
} from 'react';

export interface PinKey { clientID: number; serverID: number | null }

export interface PreviewModeValue {
    active: boolean; // the Превью tab is open
    setActive(active: boolean): void;
    pinned: PinKey | null; // the object whose card is on the canvas
    pin(key: PinKey | null): void;
    picking: boolean; // the model picker of the pinned object is open
    pickingAll: boolean; // it starts with "for all objects of this label" checked
    setPicking(picking: boolean, all?: boolean): void;
}

const PreviewModeContext = createContext<PreviewModeValue | null>(null);

export function usePreviewMode(): PreviewModeValue | null {
    return useContext(PreviewModeContext);
}

// shared by the sidebar tab, its list and the card on the canvas
export function PreviewModeProvider({ children }: { children: React.ReactNode }): JSX.Element {
    const [active, setActiveState] = useState(false);
    const [pinned, setPinned] = useState<PinKey | null>(null);
    const [picking, setPickingState] = useState(false);
    const [pickingAll, setPickingAll] = useState(false);
    const setPicking = useCallback((value: boolean, all = false) => {
        setPickingState(value);
        setPickingAll(value && all);
    }, []);
    const pin = useCallback((key: PinKey | null) => {
        setPinned(key);
        if (!key) setPicking(false);
    }, [setPicking]);
    const setActive = useCallback((value: boolean) => {
        setActiveState(value);
        if (!value) pin(null);
    }, [pin]);
    const value = useMemo(() => ({
        active, setActive, pinned, pin, picking, pickingAll, setPicking,
    }), [active, setActive, pinned, pin, picking, pickingAll, setPicking]);
    return <PreviewModeContext.Provider value={value}>{children}</PreviewModeContext.Provider>;
}
